import React, { useState, useEffect, useRef, useMemo } from 'react';
import Toolbar from '@material-ui/core/Toolbar';
import Card from '@material-ui/core/Card';
import { ITransformer } from '../../../@types/model/transformer/transformer';
import MaterialTable from '../../customComponents/materialTable/Table';
import TransformerDropdown from '../../customComponents/selector/transformer/TransformerSelector';
import TransformerReadingHelper, { IMeterReadingsView, ITransformerReading } from '../../../@types/model/transformer/reading/transformerReading';
import generalFunctions from '../../../store/general/functions';
import lodash from 'lodash';
import appFunctionsService from '../../../services/appFunctionsService';
import TransformerSPUReadingInfoDialog from './dialog/reading/spu/SPUMeterReadingInfoDialog';
import TransformerMeterReadingProblem from '../../customComponents/TransformerMeterReadingProblem';
import Tooltip from '@material-ui/core/Tooltip';
import { IRootState } from '../../../@types/redux';
import { connect } from 'react-redux';
import { EmployeeHelper, IUserSession } from '../../../@types/employee';
import { firestore } from 'firebase/app';
import SPUMeterCreateDialog from './dialog/reading/spu/SPUMeterReadingCreate';
import TransformerSPUReadingDeleteDialog from './dialog/reading/spu/SPUMeterReadingDeleteDialog';
import { Warning } from '@material-ui/icons';
import moment from 'moment';
import { Fab, Icon, IconButton } from '@material-ui/core';
import { CSVLink } from 'react-csv';
import { CSVLinkProps } from '../../../@types/csv';
import DateRangePicker from '../../customComponents/input/DateRangePicker';
import TransformerSPUMeterReportDialog from '../dialog/SPUMeterReport';
import SPUApproveDialog from './dialog/invoice/spu/SPUApproveDialog';
import Screen from '../../customComponentV2/screen/Screen';

interface ITransformerSPUMeterListProps {
    isTransformerAdmin : boolean;
    isTransformerSpuTester : boolean;

    session ?: IUserSession | null;

    transformers : Array<ITransformer>;
}

const TransformerSPUMeterListComponent = (props : ITransformerSPUMeterListProps) : React.ReactElement => {
    const [transformerNumber, setTransformerNumber] = useState<string | undefined>(undefined);
    const [readings, setReadings] = useState<Array<ITransformerReading>>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [dateFrom, setDateFrom] = useState<moment.Moment | null>(moment.utc().startOf('month').subtract(1, 'months'));
    const [dateTo, setDateTo] = useState<moment.Moment | null>(moment.utc().startOf('day'));
    const [onlyErrors, setOnlyErrors] = useState<boolean>(false);
    const [selectedRows, setSelectedRows] = useState<Array<IMeterReadingsView>>([]);
    const [showApproveDialog, setShowApproveDialog] = useState<boolean>(false);
    const [showDisapproveDialog, setShowDisapproveDialog] = useState<boolean>(false);

    const csvLink = useRef<CSVLinkProps & HTMLAnchorElement>(null);


    useEffect(() => {
        setIsLoading(true);

        const session = props.session;

        if (!session) return (() => {});

        const unsubscribe = TransformerReadingHelper.listen()
            .onSnapshot((snapshot) => {

            const fetchedDocs : Array<ITransformerReading> = [];
                
            snapshot.forEach((doc) => {
                fetchedDocs.push(doc.data());
            });

            const filteredMeterReadings = fetchedDocs.filter(x => 
                session.employee.IsTransformerAdmin || session.employee.Areas.includes(x.Division ?? '')
            );

            setReadings(filteredMeterReadings);
            setIsLoading(false);

        }, (err) => {
            generalFunctions.generalShowErrorSnackbar(err.message);
            setIsLoading(false);
        });

        return (() => unsubscribe);
    }, [props.session]);

    const getMeterReadingView = useMemo<Array<IMeterReadingsView>>(() => {
        const result : Array<IMeterReadingsView> = lodash.chain(readings)
            .filter(n => !transformerNumber || n.Electricalpoint.id === transformerNumber)
            .flatMap(n => n.TransformerMeterNumberReadings.map(reading => ({
                ...reading,
                Guid: n.GUID,
                ItemNumber: reading.ItemNumber,
                Date: n.Date?.toMillis() ?? n.CreatedOn.toMillis(),
                Conditions: n.Conditions,
                EPNumber: n.Electricalpoint.id,
                CreatedByName: n.CreatedByName,
                UpdatedByName: n.UpdatedByName,
                CreatedByEmployee: n.CreatedByEmployee,
                UpdatedByEmployee: n.UpdatedByEmployee,
                Division: n.Division,
                Group: n.Group,
                Rollover: reading.Rollover,
                CreatedBy: n.CreatedBy.id,
                UpdatedBy: n.UpdatedBy.id,
                isWeb: n.isWeb,
                SubArea: props.transformers.find(x => x.EPNumber === n.Electricalpoint.id)?.SubArea,
                PoleNumber: props.transformers.find(x => x.EPNumber === n.Electricalpoint.id)?.PoleNumber ?? undefined,
                AccountNumber: props.transformers.find(x => x.EPNumber === n.Electricalpoint.id)?.AccountNumber,
                Approved: reading.Approved,
                ApprovedOn: reading.ApprovedOn,
                ApprovedBy: reading.ApprovedBy,
                ApprovedByName: reading.ApprovedByName,
                ApprovedByEmployee: reading.ApprovedByEmployee,
            } as IMeterReadingsView)))
            .value();

        return result;
    }, [readings, transformerNumber, props.transformers]);

    const hasError = (reading : IMeterReadingsView, other ?: IMeterReadingsView | null) : string => {
        const errors : Array<string> = [];

        if (reading.Usage < 0) {
            errors.push('Transformer reading has a negative usage');
        }

        if (other) {
            errors.push('Reading already captured for selected month');
        }

        return errors.join(', ');
    };

    const getMappedData = useMemo<Array<IMeterReadingsView>>(() => {
        const result : Array<IMeterReadingsView> = lodash.chain(getMeterReadingView)
            .filter(n => !dateFrom || n.Date >= dateFrom.valueOf())
            .filter(n => !dateTo || n.Date <= dateTo.valueOf())
            .map((reading, i, all) => ({
                ...reading,
                Error: hasError(reading, lodash.find(all, x =>
                    x.EPNumber === reading.EPNumber &&
                    x.Guid !== reading.Guid &&
                    moment.utc(x.Date).format('MM-YYYY') === moment.utc(reading.Date).format('MM-YYYY')
                )),
            }))
            .sort((a, b) => b.Date - a.Date)
            .value();

        return result;
    }, [getMeterReadingView, dateFrom, dateTo]);

    const getFilteredData = useMemo<Array<IMeterReadingsView>>(() => {
        const result : Array<IMeterReadingsView> = lodash.chain(getMappedData)
            .filter(n => !onlyErrors || !!n.Error)
            .sort((a, b) => b.Date - a.Date)
            .value();

        return result;
    }, [getMappedData, onlyErrors]);

    const onEPNumberChange = (value ?: ITransformer) : void => {
        setTransformerNumber(value?.EPNumber);
    };

    const onSave = (meterReading : IMeterReadingsView) : void => {
        const session = props.session;

        if (!session) return;
        
        const readingIndex = readings.findIndex(x => x.GUID === meterReading.Guid);

        if (readingIndex < 0) return;

        const reading = readings[readingIndex];
        const meterIndex = reading.TransformerMeterNumberReadings.findIndex(x => x.ItemNumber === meterReading.ItemNumber);

        if (meterIndex < 0) return;

        const update = {
            ...reading,
            TransformerMeterNumberReadings: [
                ...reading.TransformerMeterNumberReadings,
            ],
        };

        update.TransformerMeterNumberReadings[meterIndex].Reading = meterReading.Reading;
        update.TransformerMeterNumberReadings[meterIndex].Rollover = meterReading.Rollover;
        update.TransformerMeterNumberReadings[meterIndex].ImageFileName = meterReading.ImageFileName;
        update.Conditions = [ ...meterReading.Conditions ];
        update.Date = firestore.Timestamp.fromMillis(meterReading.Date.valueOf());
        update.UpdatedBy = EmployeeHelper.doc(session.firebaseUser.uid);
        update.UpdatedByName = session.firebaseUser.displayName ?? '';
        update.UpdatedByEmployee = session.employee.EmployeeNumber ?? '';
        update.UpdatedOn = firestore.Timestamp.now();

        TransformerReadingHelper.update(update);
    };

    const onDelete = async (meterReading : IMeterReadingsView) : Promise<void> => {
        const reading = lodash
            .chain(readings)
            .filter(x => x.Electricalpoint.id === meterReading.EPNumber)
            .sortBy(x => x.Date?.toMillis() ?? x.CreatedOn.toMillis())
            .find(x => x.GUID === meterReading.Guid)
            .value();

        await TransformerReadingHelper.delete(reading);
    };
    
    const onDateSelectionChange = (start : moment.Moment | null, end : moment.Moment | null) : void => {
        setDateFrom(!start ? null : moment(start));
        setDateTo(!end ? null : moment(end));
    };

    const onShowOnlyErrorClick = () : void => {
        setOnlyErrors(!onlyErrors);
    };

    const onCSVClick = () : void => {
        if (csvLink.current) {
            csvLink.current.link.click();
        }
    };

    const getCSVData = useMemo<Array<any>>(() => {
        return getFilteredData.map(n => ({
            EPNumber: n.EPNumber,
            Date: appFunctionsService.formatDateTime(n.Date),
            Division: n.Division,
            Group: n.Group,
            Meter: n.MeterNumber,
            Reading: n.Reading,
            LastReading: n.Rollover ?
                n.Reading - n.Usage + Number(new Array(n.NumberOfDigits).fill('9').join('')) :
                n.Reading - n.Usage,
            Usage: n.Usage,
            CreatedByName: n.CreatedByName,
            CreatedByEmployee: n.CreatedByEmployee,
            Problems: n.Conditions.join('; '),
            SubArea: n.SubArea,
            AccountNumber: n.AccountNumber,
            Approved: n.Approved,
            ApprovedOn: !n.ApprovedOn ? '' : appFunctionsService.formatDateTime(n.ApprovedOn),
            ApprovedBy: n.ApprovedBy,
            ApprovedByName: n.ApprovedByName,
            ApprovedByEmployee: n.ApprovedByEmployee,
        }));
    }, [getFilteredData]);

    const onSelectionChanged = (rows : Array<IMeterReadingsView>) : void => {
        setSelectedRows(rows);
    };

    const onApproveSelectedClick = () : void => {
        setShowApproveDialog(true);
    };

    const onApproveClick = (event : React.MouseEvent<HTMLButtonElement, MouseEvent>) : void => {
        const currentReadings = getFilteredData;
        const reading = currentReadings.find(x => x.Guid === event.currentTarget.value);

        if (reading) {
            setSelectedRows([reading]);
            setShowApproveDialog(true);
        }
    };

    const onDisapproveSelectedClick = () : void => {
        setShowDisapproveDialog(true);
    };

    const onDisapproveClick = (event : React.MouseEvent<HTMLButtonElement, MouseEvent>) : void => {
        const currentReadings = getFilteredData;
        const reading = currentReadings.find(x => x.Guid === event.currentTarget.value);
        
        if (reading) {
            setSelectedRows([reading]);
            setShowDisapproveDialog(true);
        }
    };

    const onApproveClose = (result : boolean) : void => {
        if (result) {
            setSelectedRows([]);
        }
        setShowApproveDialog(false);
    };

    const onDisapproveClose = (result : boolean) : void => {
        if (result) {
            setSelectedRows([]);
        }
        setShowDisapproveDialog(false);
    };

    const { isTransformerAdmin, isTransformerSpuTester, session } = props;
    const meterView = getMeterReadingView;
    const currentReadings = getFilteredData;

    const csvName = moment().valueOf();
    const csvData = getCSVData;

    return (
        <Screen
            useAnimatedLoader
            isLoading={isLoading}
            isPadded={false}
            isScrollable={false}>
            <div className={'fdc flx1 p10 mh0 mw0 bcg0'}>
                <Toolbar>
                    <span className={'flx1'} />
                    <div style={{ paddingRight: '15px' }}>
                        {
                            !!selectedRows.length &&
                            !!selectedRows.some(n => !n.Approved) &&
                            isTransformerSpuTester &&
                            <Tooltip title='Approve Selected'>
                                <Fab color='primary' size='small' onClick={onApproveSelectedClick}>
                                    <Icon>
                                        check
                                    </Icon>
                                </Fab>
                            </Tooltip>
                        }
                    </div>
                    <div style={{ paddingRight: '15px' }}>
                        {
                            !!selectedRows.length &&
                            !!selectedRows.some(n => n.Approved) &&
                            isTransformerSpuTester &&
                            <Tooltip title='Disapprove Selected'>
                                <Fab color='primary' size='small' onClick={onDisapproveSelectedClick}>
                                    <Icon>
                                        close
                                    </Icon>
                                </Fab>
                            </Tooltip>
                        }
                    </div>
                    <div style={{ paddingRight: '15px' }}>
                        <TransformerSPUMeterReportDialog fullScreen readings={meterView} />
                    </div>
                    <div className='pr20'>
                        <Tooltip title='Show only Errors'>
                            <Fab color={onlyErrors ? 'primary' : 'default'} size='small' onClick={onShowOnlyErrorClick}>
                                <Icon className='fs20'>
                                    warning
                                </Icon>
                            </Fab>
                        </Tooltip>
                    </div>
                    <div className={'aic p5 mb10 w350'}>
                        <DateRangePicker onChange={onDateSelectionChange} start={dateFrom} end={dateTo} />
                    </div>
                    <div className={'fdr pt5 jcfe aic'}>
                        <div className='fdc flx1 w240'>
                            <TransformerDropdown
                                type='spu'
                                value={transformerNumber}
                                label='Select Transformer'
                                fullWidth
                                onChange={onEPNumberChange}
                            />
                        </div>
                    </div>
                </Toolbar>
                <Card className={'flx1 fdc mb70'}>
                    <MaterialTable<IMeterReadingsView>
                        id='transformerReadingsTable'
                        data={currentReadings}
                        isLoading={isLoading}
                        rowsPerPage={50}
                        externalSort
                        onSelectionChanged={onSelectionChanged}
                        onExportCsvClick={onCSVClick}
                        selectedRows={selectedRows}
                        selectable={isTransformerSpuTester}
                        columns={[{
                            header: '',
                            field: 'EPNumber',
                            renderCell: row => (
                                <div className=' fdr aic'>
                                    <TransformerSPUReadingInfoDialog key={`${row.Guid}_${row.ItemNumber}_edit`}
                                        reading={row}
                                        enabled={(isTransformerAdmin || !row.Approved)}
                                        onSave={onSave}
                                    />
                                    {
                                        (!row.Approved) &&
                                        <TransformerSPUReadingDeleteDialog key={`${row.Guid}_${row.ItemNumber}_delete`}
                                            reading={row}
                                            onDelete={onDelete}
                                        />

                                    }
                                    {
                                        !!row.isWeb &&
                                        <Icon className='cp ml10'>
                                            laptop
                                        </Icon>                                            
                                    }
                                    {
                                        !row.isWeb &&
                                        <Icon className='cp ml10'>
                                            smartphone
                                        </Icon>                                            
                                    }
                                    {
                                        !!row.Error &&
                                        <Tooltip title={row.Error}>
                                            <Warning className={'cy pl10'}/>
                                        </Tooltip>
                                    }
                                </div>
                            ),
                        }, {
                            header: 'Date',
                            field: 'Date',
                            width: 175,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: row => (<span>{appFunctionsService.formatDateTimeToDateOnly(row.Date)}</span>),
                        }, {
                            header: 'EP Point',
                            field: 'EPNumber',
                            width: 145,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Pole Nr',
                            field: 'PoleNumber',
                            width: 145,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Area',
                            field: 'Division',
                            width: 145,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Group',
                            field: 'Group',
                            width: 145,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Meter',
                            field: 'MeterNumber',
                            width: 145,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Meter Reading (kWh)',
                            field: 'Reading',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Last Reading (kWh)',
                            field: 'Usage',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: row => (
                                <span>
                                    {
                                        row.Rollover ?
                                            row.Reading - row.Usage + Number(new Array(row.NumberOfDigits).fill('9').join('')) :
                                            row.Reading - row.Usage
                                    }
                                </span>),
                        }, {
                            header: 'Usage',
                            field: 'Usage',
                            width: 145,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Created By',
                            field: 'CreatedByName',
                            width: 145,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: row => (
                                <Tooltip title={row.CreatedByEmployee ?? ''}>
                                    <div>
                                        {
                                            row.CreatedByName
                                        }
                                    </div>
                                </Tooltip>
                            ),
                        }, {
                            header: 'Problems',
                            field: 'Conditions',
                            renderCell: row => (<span className='fdr cw'>{row.Conditions.map((n, i) => (<TransformerMeterReadingProblem key={`${i}_${row.EPNumber}_problem`} text={n} />))}</span>),
                        }, {
                            header: 'APPROVE',
                            width: '175px',
                            renderCell: row => (
                                <div>
                                    {
                                        !row.Approved &&
                                        <Tooltip title='Approve'>
                                            <IconButton value={row.Guid} onClick={onApproveClick}>
                                                <Icon className={'cpd'}>
                                                    check_circle
                                                </Icon>
                                            </IconButton>
                                        </Tooltip>
                                    }
                                    {
                                        !!row.Approved && !!row.ApprovedOn &&
                                        <Tooltip title={`${appFunctionsService.formatDateTime(row.ApprovedOn)}`}>
                                            <span>{row.ApprovedByName}</span>
                                        </Tooltip>
                                    }
                                    {
                                        !!row.Approved && !!row.ApprovedOn &&
                                        <Tooltip title='Disapprove'>
                                            <IconButton value={row.Guid} onClick={onDisapproveClick}>
                                                <Icon className={'cr'}>
                                                    close
                                                </Icon>
                                            </IconButton>
                                        </Tooltip>
                                    }
                                </div>
                            ),
                        }]}
                    />
                </Card>
                <div className='fdr jcfe'>
                    <SPUMeterCreateDialog aria-label='add' session={session} readings={currentReadings} />
                </div>
                <CSVLink
                    data={csvData}
                    headers={[
                        {
                            key: 'EPNumber',
                            label: 'EP Number',
                        },
                        {
                            key: 'Date',
                            label: 'Date',
                        },
                        {
                            key: 'Division',
                            label: 'Area',
                        },
                        {
                            key: 'SubArea',
                            label: 'Sub Area',
                        },
                        {
                            key: 'Group',
                            label: 'Group',
                        },
                        {
                            key: 'AccountNumber',
                            label: 'Account Nr',
                        },
                        {
                            key: 'Meter',
                            label: 'Meter',
                        },
                        {
                            key: 'Reading',
                            label: 'Reading',
                        },
                        {
                            key: 'LastReading',
                            label: 'LastReading',
                        },
                        {
                            key: 'Usage',
                            label: 'Usage',
                        },
                        {
                            key: 'CreatedByName',
                            label: 'CreatedByName',
                        },
                        {
                            key: 'CreatedByEmployee',
                            label: 'CreatedByEmployee',
                        },
                        {
                            key: 'Problems',
                            label: 'Problems',
                        },
                        {
                            key: 'Approved',
                            label: 'Approved',
                        },
                        {
                            key: 'ApprovedByName',
                            label: 'Approved By Name',
                        },
                        {
                            key: 'ApprovedByEmployee',
                            label: 'Approved By Employee',
                        },
                        {
                            key: 'ApprovedOn',
                            label: 'Approved On',
                        },
                    ]}
                    filename={`${csvName}_spu_transformers.csv`}
                    className='dn'
                    ref={csvLink}
                    target='_blank'/>

                {
                    session &&
                    <SPUApproveDialog
                        open={showApproveDialog}
                        onClose={onApproveClose}
                        values={selectedRows}
                        title='Approve Readings'
                        readings={readings}
                        session={session}
                    />
                }
                {
                    session &&
                    <SPUApproveDialog
                        disapprove
                        open={showDisapproveDialog}
                        onClose={onDisapproveClose}
                        values={selectedRows}
                        title='Disapprove Readings'
                        readings={readings}
                        session={session}
                    />
                }
            </div>
        </Screen>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        isTransformerAdmin: !!state.auth.session?.employee.IsTransformerAdmin,
        isTransformerSpuTester: !!state.auth.session?.employee.IsTransformerSpuTester,
        session: state.auth.session,
        transformers: state.transformer.transformers,
    };
};

const TransformerSPUMeterList = connect(
    mapStateToProps,
)(TransformerSPUMeterListComponent);

export default TransformerSPUMeterList;
